// packages/agent-kit/src/useAgent.ts
import useSWR from 'swr';
import { useCallback } from 'react';
import { agentService, isApiError } from '@dopomogai/api-client';
import type { AgentResponse, AgentUpdatePayload, UUID } from '@dopomogai/types';
import { AgentKitError } from '../types';

const agentFetcher = async ([, agentId]: [string, UUID]): Promise<AgentResponse> => {
  const response = await agentService.getAgent(agentId);
  if (isApiError(response)) {
    throw new AgentKitError(`Failed to fetch agent ${agentId}`, response);
  }
  return response;
};

/**
 * A hook to fetch and manage a single agent's details.
 */
export const useAgent = (agentId: UUID | null) => {
  const cacheKey = agentId ? [`agents/${agentId}`, agentId] : null;
  const { data, error, isLoading, mutate } = useSWR<AgentResponse, AgentKitError>(
    cacheKey,
    agentFetcher
  );

  /**
   * Updates the agent's details.
   * Handles the API call and automatically revalidates the local SWR cache.
   */
  const updateAgent = useCallback(async (payload: AgentUpdatePayload) => {
    if (!agentId) throw new AgentKitError("Agent ID is required to update.");

    // Optimistically update the UI for a faster user experience
    if (data) {
      mutate({ ...data, ...payload }, false);
    }

    const response = await agentService.updateAgent(agentId, payload);
    if (isApiError(response)) {
      // If the API call fails, revert the optimistic update
      if (data) mutate(data, false);
      throw new AgentKitError(`Failed to update agent ${agentId}`, response);
    }
    
    // Trigger a re-fetch from the server to get the canonical state
    mutate();
    return response;
  }, [agentId, data, mutate]);

  return {
    agent: data,
    isLoading,
    error,
    updateAgent,
    mutate,
  };
};